import { useReportStore } from '@/entities/report';
import { reportStntQueries } from '@/entities/report/model/report-stnt.queries';
import {
    reportMainTabStateTypeGuard,
    reportSubTabStateTypeGuard,
} from '@/entities/report/model/report.types';
import { ReportTemplate, TabMenu } from '@/shared/ui';
import { showToast } from '@/shared/utils/toast';
import { StudentAiReview } from '@/widgets/report/ai-review';
import {
    ReportStntContentsViewer,
    ReportStntThumbList,
} from '@/widgets/report/detail/table-body/stnt';
import { ReportHeaderLeft, ReportHeaderStntRight } from '@/widgets/report/detail/table-header';
import { ReportDetailHeader } from '@/widgets/section-header';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { useLoginStore } from '@/shared/lib/store/model/login.store';
export const Route = createFileRoute('/eval/report/$evalId/$stntId')({
    component: RouteComponent,
    loader: ({ params }) => {
        return { evalId: Number(params.evalId), stntId: params.stntId.toString() };
    },
});

function RouteComponent() {
    const { setNickNameToken } = useLoginStore();
    const { evalId, stntId } = Route.useLoaderData();
    const { stntReport, setStntReport } = useReportStore();

    const { data, isLoading } = reportStntQueries.detail.getStntReportEvalResultDetail({
        evlId: evalId,
        stntId: stntId,
    });

    const { data: aiReviewData } = reportStntQueries.review.getReportEvalReviewInfo({
        evlId: evalId,
        userId: stntId,
    });

    useEffect(() => {
        async function fetchAndSetUser() {
            await setNickNameToken(stntId);
        }
        fetchAndSetUser();
    }, [stntId]);

    // 메인 탭 변경
    const mainTabChange = useCallback(
        (val: string) => {
            if (reportMainTabStateTypeGuard(val)) {
                setStntReport({ mainTab: val });
            }
        },
        [setStntReport]
    );

    // 서브 탭 변경
    const subTabChange = useCallback(
        (val: string) => {
            if (reportSubTabStateTypeGuard(val)) {
                // 콘텐츠 정보 클릭 시 학생 index 초기화
                if (val === 'contents') {
                    setStntReport({ stntsListIdx: undefined });
                    if (stntReport.thumbListIdx === undefined) {
                        setStntReport({ thumbListIdx: 0 });
                    }
                }
                setStntReport({ subTab: val });
            }
        },
        [setStntReport, stntReport.thumbListIdx]
    );

    // 썸네일 리스트 index 변경
    const tumbListChange = useCallback(
        (idx: number) => {
            setStntReport({ thumbListIdx: idx });
        },
        [setStntReport]
    );

    // 학생 index 변경
    const studentChange = useCallback(
        (idx: number) => {
            setStntReport({ stntsListIdx: idx });
        },
        [setStntReport]
    );

    if (isLoading) return null;
    if (!data) return null;

    const scoreType = data?.evlStdr;
    const score = data?.stntTotScr;
    const submAt = data?.evlInfoList?.[0]?.mdulEvlInfo?.submAt;

    const selectedModule =
        stntReport.thumbListIdx === 0 || stntReport.thumbListIdx
            ? data?.evlInfoList[stntReport.thumbListIdx ?? 0]
            : undefined;
    const contentsNum =
        stntReport.thumbListIdx === 0 || stntReport.thumbListIdx ? stntReport.thumbListIdx + 1 : 1;

    const setType = stntReport.usage === '클래스' ? '1' : stntReport?.usage === 'URL' ? '3' : '2';

    return (
        <div className="contwrap">
            <div className="contInner">
                <ReportDetailHeader setType={setType} title={data?.evlNm} />

                <TabMenu
                    reset={true}
                    className="tab-size1 con-mt3"
                    tabItems={[
                        { tabName: '상세 보기', value: '상세 보기' },
                        {
                            tabName: '총평',
                            value: 'AI 총평',
                            disabledFn:
                                aiReviewData?.stdtPrntRlsAt === 'N' ||
                                (aiReviewData?.stdtPrntRlsAt === 'Y' && aiReviewData?.genrvw === '')
                                    ? () =>
                                          showToast(
                                              <span
                                                  style={{ whiteSpace: 'nowrap', color: 'white' }}
                                              >
                                                  총평이 작성되지 않았거나, 비공개 상태입니다.
                                              </span>,
                                              2000,
                                              'error'
                                          )
                                    : undefined,
                        },
                    ]}
                    selected={stntReport.mainTab}
                    onChange={mainTabChange}
                />
                <div className="con-mt3">
                    <ReportTemplate
                        // 테이블 해더: 항목, 점수(평가기준)
                        headerLeft={<ReportHeaderLeft />}
                        // 테이블 해더: 콘텐츠 정보, 학생 이름
                        headerRight={
                            <ReportHeaderStntRight
                                scoreType={scoreType}
                                score={score}
                                submAt={submAt}
                                selected={stntReport.subTab}
                                subTabChange={subTabChange}
                                studentChange={studentChange}
                                // mainTab={stntReport.mainTab}
                                // isStudent={true}
                            />
                        }
                        // 테이블 바디: 썸네일 리스트
                        bodyLeft={
                            stntReport.mainTab !== 'AI 총평' && (
                                <ReportStntThumbList
                                    thumbList={data?.evlInfoList}
                                    selected={stntReport.subTab}
                                    selectedThumbIndex={stntReport.thumbListIdx}
                                    onThumbClick={tumbListChange}
                                    isStudent={true}
                                />
                            )
                        }
                        // 테이블 바디: 콘텐츠, 뷰어
                        bodyRight={
                            stntReport.mainTab !== 'AI 총평' && (
                                <ReportStntContentsViewer
                                    setsId={data?.setsId}
                                    selectedThumbContent={selectedModule}
                                    selectedContentsNumber={contentsNum}
                                    selectedSubTab={stntReport.subTab}
                                    isStudent={true}
                                />
                            )
                        }
                        bodyTotal={
                            stntReport.mainTab === 'AI 총평' && (
                                <StudentAiReview genrvw={aiReviewData?.genrvw ?? ''} />
                            )
                        }
                    />
                </div>
            </div>
        </div>
    );
}
